import React, { useCallback, useEffect, useRef, useMemo } from 'react';
import { 
  Grid, 
  GridColumn, 
  GridSelectionChangeEvent,
  GridHeaderSelectionChangeEvent,
  GridItemChangeEvent,
  GridCellProps,
  GridHandle
} from "@progress/kendo-react-grid";
import { SelectDescriptor } from '@progress/kendo-react-data-tools';
import { Groups, Endpoints, GroupStatus } from '../../../../Models/Assets/AssetTemplateReadDto';
import { DropDownList, DropDownListChangeEvent } from "@progress/kendo-react-dropdowns";
import { enMessages } from '../../../../messages/en-US';
import { useLocalization } from '@progress/kendo-react-intl';

interface Props {
  groups: Groups[];
  onGroupSelect?: (groupId: string | null) => void;
  selectedGroupId?: string | null;
  onGroupsChange: (groups: Groups[]) => void;
  assetData?: any;
  showEnabledColumn?: boolean; // Optional flag to show/hide the Status column (default: true)
}

const DATA_ITEM_KEY = 'id';

const statusOptions: { label: string; value: GroupStatus }[] = [
  { label: 'Enabled', value: 'Active' },
  { label: 'Disabled', value: 'Inactive' }
];

function groupStatus(group: Groups): GroupStatus {
  const status = (group as Groups & { status?: GroupStatus }).status;
  return status ?? 'Inactive';
}

const GroupsConfigurationTableComponent: React.FC<Props> = ({
  groups,
  onGroupSelect,
  selectedGroupId,
  onGroupsChange,
  assetData = { endpoints: [] },
  showEnabledColumn = true
}) => {
  const gridRef = useRef<GridHandle>(null);
  const [select, setSelect] = React.useState<SelectDescriptor>(() => {
    return selectedGroupId ? { [selectedGroupId]: true } : {};
  });
  const localizationService = useLocalization();

  // Always editable - mark all groups with inEdit: true
  const editableGroups = useMemo(() => {
    return groups.map(group => ({ ...group, inEdit: true }));
  }, [groups]);

  useEffect(() => {
    if (selectedGroupId === '0') {
      const newSelect: SelectDescriptor = {};
      groups.forEach((group: Groups) => {
        newSelect[group.id] = true;
      });
      setSelect(newSelect);
    } else if (selectedGroupId) {
      setSelect({ [selectedGroupId]: true });
      const selectedIndex = groups.findIndex(group => group.id === selectedGroupId);
      if (selectedIndex >= 0 && gridRef.current) {
        gridRef.current.scrollIntoView({ rowIndex: selectedIndex });
      }
    } else {
      setSelect({});
    }
  }, [selectedGroupId, groups]);

  const handleSelectionChange = useCallback((event: GridSelectionChangeEvent) => {
    const selectedGroup = event.dataItem || event.startDataItem;
    event.nativeEvent?.stopPropagation();
    if (selectedGroup) {
      const newGroupId = selectedGroup.id;
      if (newGroupId !== selectedGroupId) {
        setSelect({ [newGroupId]: true });
        onGroupSelect?.(newGroupId);
      }
    }
  }, [selectedGroupId, onGroupSelect]);

  const handleHeaderSelectionChange = useCallback((event: GridHeaderSelectionChangeEvent) => {
    event.nativeEvent?.stopPropagation();
    const isSelected = event.nativeEvent.target.checked;
    const newSelect: SelectDescriptor = {};
    if (isSelected) {
      groups.forEach((group: Groups) => {
        newSelect[group.id] = true;
      });
      onGroupSelect?.('0');
    } else {
      onGroupSelect?.(null);
    }
    setSelect(newSelect);
  }, [groups, onGroupSelect]);

  const handleItemChange = (event: GridItemChangeEvent) => {
    const newGroups = groups.map((group: Groups) =>
      group.id === event.dataItem.id ? { ...group, [event.field!]: event.value } : group
    );
    
    // Notify parent component
    onGroupsChange(newGroups);
  };


  const devicesCell = (props: GridCellProps) => {
    const devices = assetData.endpoints.filter(
      (endpoint: Endpoints) => endpoint.groupId === props.dataItem.id
    );
    return <td><div>{devices.length}</div></td>;
  };

  const statusCell = (props: GridCellProps) => {
    const { dataItem } = props;
    const status = groupStatus(dataItem as Groups);
    const handleDropDownChange = (e: DropDownListChangeEvent) => {
      handleItemChange({
        dataItem: dataItem,
        field: "status",
        value: e.target.value.value
      } as GridItemChangeEvent);
    };
    return (
      <td>
        <DropDownList
          style={{ width: "100%" }}
          data={statusOptions}
          textField="label"
          dataItemKey="value"
          value={statusOptions.find(opt => opt.value === status) ?? statusOptions.find(opt => opt.value === 'Inactive')}
          onChange={handleDropDownChange}
        />
      </td>
    );
  };

  const EmptyHeaderCell = () => <th></th>;

  return (
    <div className="flex flex-col h-full checkbox-outline-soft-override">
      <div className="flex justify-between align-middle mb-2">
        <div className="text-xl font-bold">{localizationService.toLanguageString('custom.groups', enMessages.custom.groups)}</div>
      </div>
      <div className="flex-1 min-h-0 overscroll-contain">
        <Grid
          ref={gridRef}
          data={editableGroups}
          style={{ height: '100%' }}
          dataItemKey={DATA_ITEM_KEY}
          selectable={{
            enabled: true,
            mode: 'single'
          }}
          select={select}
          onSelectionChange={handleSelectionChange}
          onHeaderSelectionChange={handleHeaderSelectionChange}
          onItemChange={handleItemChange}
          editable={true}
          editField="inEdit"
        >
          <GridColumn 
            columnType="checkbox" 
            width="50px"
            cells={{
              headerCell: EmptyHeaderCell
            }}
            editable={false}
          />
          <GridColumn 
            field="name" 
            title={localizationService.toLanguageString('custom.name', enMessages.custom.name)}
            editable={false}
          />
          <GridColumn 
            field="devices" 
            title={localizationService.toLanguageString('custom.points', enMessages.custom.points)}
            cells={{
              data: devicesCell
            }}
            editable={false}
          />
          {showEnabledColumn && (
            <GridColumn 
              field="status" 
              title={localizationService.toLanguageString('custom.status', enMessages.custom.status)}
              cells={{
                data: statusCell
              }}
            />
          )}
        </Grid>
      </div>
    </div>
  );
};

export default GroupsConfigurationTableComponent; 